import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { formatMinor, minorToInput, parseToMinor, currencySymbol } from "../../lib/commercial";

export const FieldRow = ({ label, help, children, testid }) => (
  <div data-testid={testid} className="space-y-1.5">
    <Label className="text-slate-600 text-sm">{label}</Label>
    {children}
    {help && <p className="text-xs text-slate-400">{help}</p>}
  </div>
);

export const NullableNumber = ({ value, onChange, placeholder = "Illimité / non applicable", testid }) => (
  <div>
    <Input
      data-testid={testid}
      type="text"
      inputMode="numeric"
      value={value === null || value === undefined ? "" : String(value)}
      placeholder={placeholder}
      onChange={(e) => {
        const t = e.target.value.trim();
        if (t === "") return onChange(null);
        const n = parseInt(t.replace(/\s/g, ""), 10);
        onChange(isNaN(n) ? null : n);
      }}
      className="bg-slate-50 border-slate-200"
    />
    {(value === null || value === undefined) && (
      <p className="text-xs text-amber-600 mt-1">Non défini → illimité ou sur mesure</p>
    )}
  </div>
);

export const MoneyField = ({ initialMinor, currency, onMinorChange, testid, placeholderHint, disabled }) => {
  const [preview, setPreview] = useState(formatMinor(initialMinor, currency));
  return (
    <div>
      <div className="relative">
        <Input
          data-testid={testid}
          type="text"
          inputMode="decimal"
          disabled={disabled}
          defaultValue={minorToInput(initialMinor, currency)}
          placeholder={placeholderHint || "—"}
          onChange={(e) => {
            const m = parseToMinor(e.target.value, currency);
            onMinorChange(m);
            setPreview(formatMinor(m, currency));
          }}
          className="bg-slate-50 border-slate-200 pr-16 disabled:opacity-50"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
          {currencySymbol(currency)}
        </span>
      </div>
      {!disabled && (
        <p className="text-xs text-slate-500 mt-1">
          <span className="font-semibold text-slate-700" data-testid={`${testid}-preview`}>{preview}</span>
        </p>
      )}
    </div>
  );
};

export const SwitchRow = ({ label, checked, onChange, testid, help }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
    <div className="min-w-0">
      <div className="text-sm text-slate-800">{label}</div>
      {help && <div className="text-xs text-slate-400">{help}</div>}
    </div>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </div>
);

export const StatusBadge = ({ ok, yes, no, testid }) => (
  <Badge
    variant="outline"
    data-testid={testid}
    className={ok
      ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
      : "bg-slate-500/10 text-slate-500 border-slate-400/30"}
  >
    {ok ? yes : no}
  </Badge>
);
